/**
 * React Query hooks for the native Skills hub + detail screens.
 *
 * Why: the hub previously re-fetched the whole list on every focus (a top
 * spinner that pushed content down on back-navigation) and reused one dataset
 * across the Explore / My skills tabs (a flash of the wrong shelf on switch).
 *
 * These hooks give each shelf/filter combination its own stable query key so
 * cached pages render instantly on return (no spinner, no layout shift) and a
 * tab switch can only ever show ITS shelf+type's cache or a skeleton: never
 * another tab's rows. Pagination is preserved via `useInfiniteQuery`
 * (accumulated pages, appended without clearing).
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import type { SkillListResult } from '@tabbio/types';
import {
  getSkill,
  getSkillContent,
  installSkill,
  listSkills,
  uninstallSkill,
} from '../../lib/skills-api';
import { CHAT_STARTER_SKILLS_LIMIT } from '../assistant-ui/chat-starter-skills';

// The launch catalog is intentionally compact. Fetch the full set so local
// country/level/category filters never hide matches that sit on a later page.
export const SKILLS_PAGE_SIZE = 50;

/** Cache freshness: long enough that back-nav within a session never refetches. */
const SKILLS_STALE_TIME = 1000 * 60 * 2;

export type SkillsTierParam = 'free' | 'plus' | 'pro';

export interface SkillsInfiniteParams {
  /** Backend shelf alias: 'explore' | 'own'. Part of the query key. */
  shelf: string;
  /** Debounced free-text query (server-side). */
  q?: string;
  /** Server locale filter: only 'en' | 'ar' (bilingual filters client-side). */
  locale?: 'en' | 'ar';
  /** Plan / tier filter (server-side). */
  tier?: SkillsTierParam;
  /** cv | other. Server-side shelf-of-purpose filter (create-CV skill picker). */
  type?: 'cv' | 'other';
}

/**
 * Paginated skills for one shelf/filter combination. The query key encodes the
 * shelf and type so no two hub tabs share a cache entry. `placeholderData`
 * keeps the previous rows visible while re-filtering WITHIN the same
 * shelf+type, but drops them on a shelf or type change so another tab can only
 * show its own cache or a skeleton.
 */
export function useSkillsInfinite(params: SkillsInfiniteParams) {
  const { shelf, q, locale, tier, type } = params;
  return useInfiniteQuery<
    SkillListResult,
    Error,
    InfiniteData<SkillListResult>,
    (string | undefined)[],
    string | undefined
  >({
    queryKey: ['skills', shelf, q || '', locale || '', tier || '', type || ''],
    queryFn: ({ pageParam }) =>
      listSkills({
        shelf,
        q: q || undefined,
        locale,
        tier,
        type,
        cursor: pageParam,
        limit: SKILLS_PAGE_SIZE,
      }),
    initialPageParam: undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: SKILLS_STALE_TIME,
    // Key positions, hard-coded: [0]'skills' [1]shelf [2]q [3]locale [4]tier
    // [5]type. Both shelf AND type must match: the Explore and Custom CV tabs
    // share shelf 'explore' and differ only at [5], so a shelf-only guard
    // flashes Explore rows into the CV tab on switch. `q` stays out of the
    // guard on purpose (holding rows during a debounced search is intended).
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === shelf &&
      previousQuery?.queryKey[5] === (type || '')
        ? previous
        : undefined,
  });
}

/** Cached skill detail so re-opening a skill renders instantly from cache. */
export function useSkillDetail(skillId: string) {
  return useQuery({
    queryKey: ['skill', skillId],
    queryFn: () => getSkill(skillId),
    enabled: skillId.length > 0,
    staleTime: SKILLS_STALE_TIME,
  });
}

/** Cached SKILL.md markdown for the detail "How it works" section. */
export function useSkillContent(skillId: string) {
  return useQuery({
    queryKey: ['skill-content', skillId],
    queryFn: () => getSkillContent(skillId),
    enabled: skillId.length > 0,
    staleTime: SKILLS_STALE_TIME,
  });
}

/** Installed + authored skills used by My skills and the chat slash menu. */
export function useInstalledSkills() {
  return useQuery({
    queryKey: ['skills', 'installed'],
    queryFn: () => listSkills({ shelf: 'own', limit: 50 }),
    staleTime: SKILLS_STALE_TIME,
  });
}

/**
 * Every skill the authenticated user may see, for Agent Chat slash discovery.
 *
 * The catalog endpoint is cursor-paginated. A slash menu has to search the
 * whole visible catalog, not just the first page or the user's installed shelf,
 * so this query drains every page into one stable cached result.
 */
export function useAllVisibleSkills(enabled = true) {
  return useQuery({
    queryKey: ['skills', 'all-visible'],
    queryFn: async (): Promise<SkillListResult> => {
      const skills: SkillListResult['skills'] = [];
      const seenCursors = new Set<string>();
      let cursor: string | undefined;

      do {
        const page = await listSkills({
          cursor,
          limit: SKILLS_PAGE_SIZE,
        });
        skills.push(...page.skills);

        const nextCursor = page.nextCursor ?? undefined;
        if (!nextCursor || seenCursors.has(nextCursor)) {
          cursor = undefined;
          break;
        }
        seenCursors.add(nextCursor);
        cursor = nextCursor;
      } while (cursor);

      return { skills, nextCursor: null };
    },
    enabled,
    staleTime: SKILLS_STALE_TIME,
  });
}

/** Popular catalog skills shown in the empty Agent Chat onboarding state. */
export function useChatStarterSkills() {
  return useQuery({
    queryKey: ['skills', 'chat-starters'],
    queryFn: () =>
      listSkills({
        shelf: 'explore',
        limit: CHAT_STARTER_SKILLS_LIMIT,
      }),
    staleTime: SKILLS_STALE_TIME,
  });
}

export function useSkillInstallationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      skillId,
      install,
    }: {
      skillId: string;
      install: boolean;
    }) => (install ? installSkill(skillId) : uninstallSkill(skillId)),
    onSuccess: async (result) => {
      queryClient.setQueryData(['skill', result.skillId], (current: unknown) =>
        current && typeof current === 'object'
          ? {
              ...current,
              installed: result.installed,
              installCount: result.installCount,
            }
          : current,
      );
      await queryClient.invalidateQueries({ queryKey: ['skills'] });
    },
  });
}
